import clsx from 'clsx';
import { useField } from 'formik';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { KTIcon } from '../../helpers';

interface WebcamPhotoFieldProps {
  name: string;
  width?: number;
  height?: number;
  facingMode?: 'user' | 'environment';
  onChange?: (value: string | null) => void;
}

const WebcamPhotoField: React.FC<WebcamPhotoFieldProps> = ({
  name,
  width = 320,
  height = 240,
  facingMode = 'environment',
  onChange,
}) => {
  const [field, meta] = useField(name);
  const webcamRef = useRef<Webcam>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(
    field.value ?? undefined
  );
  const [isWebcamOpen, setIsWebcamOpen] = useState(false);
  const { t } = useTranslation();

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
      onChange?.(imageSrc);
      setIsWebcamOpen(false);
    }
  }, [onChange]);

  const retakePhoto = () => {
    setCapturedImage(null);
    onChange?.(null);
    setIsWebcamOpen(true);
  };

  const openWebcam = () => {
    setIsWebcamOpen(true);
  };

  const closeWebcam = () => {
    setIsWebcamOpen(false);
  };

  const removePhoto = () => {
    setCapturedImage(null);
    onChange?.(null);
  };

  return (
    <div className='webcam-photo-field'>
      {!capturedImage && !isWebcamOpen && (
        <div
          className={clsx(
            'dropzone-area border border-dashed border-gray-300 rounded p-8 text-center cursor-pointer position-relative',
            'hover-elevate-up d-flex flex-column align-items-center justify-content-center',
            { 'border-danger': meta.touched && meta.error }
          )}
          onClick={openWebcam}
          style={{ height: '175px', backgroundColor: '#f8f9fa' }}
        >
          <div className='symbol symbol-60px symbol-circle mb-4'>
            <div className='symbol-label bg-light-primary'>
              <KTIcon iconName={'picture'} className={'fs-3'} />
            </div>
          </div>
          <div className='text-gray-800 fw-bold fs-5 mb-2'>
            {t('Click to take a photo')}
          </div>
          <div className='text-gray-500 fs-7'>
            {t('Use your camera to capture an image')}
          </div>
        </div>
      )}

      {isWebcamOpen && (
        <div className='webcam-container border border-dashed border-gray-300 rounded p-4 text-center'>
          <div className='webcam-wrapper position-relative d-inline-block'>
            <Webcam
              ref={webcamRef}
              audio={false}
              width={width}
              height={height}
              screenshotFormat='image/png'
              screenshotQuality={0.8}
              minScreenshotHeight={800}
              minScreenshotWidth={800}
              // videoConstraints={{
              //   width: 800,
              //   height: 800,
              //   facingMode
              // }}
              className='webcam-video rounded'
              style={{ maxWidth: '100%' }}
            />
            <div className='webcam-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
              <div
                className='capture-frame border border-2 border-white rounded'
                style={{
                  width: '80%',
                  height: '80%',
                  background: 'transparent',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)',
                }}
              ></div>
            </div>
          </div>
          <div className='webcam-controls mt-3'>
            <button
              type='button'
              onClick={capture}
              className='btn btn-sm btn-success me-2'
            >
              <KTIcon iconName={'picture'} className='fs-3' />
              {t('Capture')}
            </button>
            <button
              type='button'
              onClick={closeWebcam}
              className='btn btn-sm btn-secondary'
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}

      {capturedImage && (
        <div className='captured-image-container border border-dashed border-gray-300 rounded p-4 position-relative'>
          <div className='image-preview-wrapper position-relative d-flex justify-content-center'>
            <img
              src={capturedImage}
              alt='Captured'
              className='captured-image rounded shadow'
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'cover',
              }}
            />
            <button
              type='button'
              onClick={removePhoto}
              className='btn btn-sm btn-icon btn-danger position-absolute top-0 end-0 m-2'
              style={{ zIndex: 10 }}
            >
              <KTIcon iconName={'minus'} className='fs-3' />
            </button>
          </div>
          <div className='image-controls text-center mt-4'>
            <button
              type='button'
              onClick={retakePhoto}
              className='btn btn-light btn-lg me-3'
            >
              <i className='fas fa-redo me-2'></i>
              {t('Retake')}
            </button>
            <div className='text-gray-600 fs-7 mt-2'>
              {t('Photo captured successfully')}
            </div>
          </div>
        </div>
      )}

      {meta.touched && meta.error && (
        <div className='invalid-feedback d-block mt-2'>
          <span>{String(meta.error)}</span>
        </div>
      )}
    </div>
  );
};

export default WebcamPhotoField;
